/**
 * Générateur — Identifier la structure d'une expression littérale (somme,
 * produit).
 * Attendu ID : 158 | Niveau : 4ème | Domaine : Calcul littéral (O22)
 *
 * Source : doc prof « TANOED EXERCISEUR … 4EME », NOMBRE ET CALCUL, cluster 3,
 * O22. Le texte du doc est MUET pour cet objectif (parse §5) ; le critère de
 * réussite vient de l'image « Exemples de réussite » (page30) :
 *   « Il identifie 3x + 12 comme une somme et 3(x + 4) comme un produit. »
 * → BANQUE construite fidèle à ce critère (décision validée avec l'utilisatrice,
 *   comme O18/O19). Mécanique héritée de gen_935 (5e, même objectif O37),
 *   enrichie de familles 4e (termes en x², produit lettre × parenthèse).
 *
 * Plan de cartes d'origine (validé 2026-07-14, historique : la carte « fiche »
 * et le QCM « corrige l'affirmation » sont sortis le 2026-09-23, voir plus bas) :
 *   ident   — l'image du doc : relier chaque expression à « somme »/« produit »
 *             + qcm_unique (nature d'UNE expression). La nature dérive de la
 *             FAMILLE génératrice (jamais étiquetée à la main).
 *   fiche   — fiche d'identité (nature, nb de termes/facteurs, coefficient de x,
 *             terme constant) en drag + qcm fermé.
 *   vf      — tableau vrai/faux (« … est un produit/une somme ») + qcm
 *             « corrige l'affirmation fausse ».
 *
 * ── Revue 2026-09-23 (vague 153-163) ──────────────────────────────────────
 * Plafond, attendus de fin de 4e (Eduscol, p. 4) : « Il identifie la structure
 * d'une expression littérale (somme, produit). » Exemple de réussite : « Il
 * identifie 3x + 12 comme une somme et 3(x + 4) comme un produit. » Le cours de
 * 4e range les différences parmi les sommes (2x − 9 = 2x + (−9) ; 100 − 7x =
 * 100 + (−7x)) et ne connaît ni quotient, ni coefficient, ni terme constant.
 *   - QUOTIENT SORTI de partout (famille, catégorie du classer, choix du QCM,
 *     lignes du Vrai/Faux, mot du libellé) : hors plafond, et « 8y/7 » se lit
 *     aussi (8/7)y, un produit, soit deux rangements défendables.
 *   - Carte « La fiche d'identité d'une expression » SORTIE
 *     (fiche_texte_trou_drag, fiche_qcm_unique) : « coefficient » et « terme
 *     constant » ne sont ni dans l'attendu ni dans le cours, et le trou
 *     « nombre de termes / facteurs » valait 2 dans 100 % des tirages.
 *   - QCM « corrige l'affirmation » SORTI (vf_qcm_unique) : un distracteur sur
 *     trois reprenait l'affirmation que l'énoncé déclarait fausse, un autre
 *     était le quotient, il restait de la lecture ; redite du QCM de nature.
 *   - Classer recadré : deux catégories, Somme et Produit, trois expressions
 *     chacune, UNE lettre par tirage, familles du cours et de gen_1229, au moins
 *     une somme À PARENTHÈSES par tirage : la conception « des parenthèses,
 *     donc un produit » classait juste 50,6 % des tirages, elle se trompe
 *     désormais à chaque fois. Consigne sans la règle ni la parenthèse
 *     « règles de priorité » : la règle ira au coup de pouce, comme sur 1229.
 *     Repli fixe remplacé par un re-tirage borné.
 *   - QCM de nature remplacé par « Laquelle de ces expressions est un produit ? »
 *     (ou « … une somme ? »), modèle genLaquelle de gen_1229 : quatre
 *     expressions d'une même lettre, une seule de la nature demandée ;
 *     familles nommées, contrat exporté (REGLES_DISTRACTEURS_QCM). Sans le
 *     quotient, l'ancien QCM n'avait plus que deux choix, et « parenthèses donc
 *     produit » y réussissait 88,5 %. C'est le seul QCM de l'attendu.
 *   - Vrai/Faux recadré : lignes tirées dans les mêmes familles, une lettre par
 *     tableau, une à trois vraies (jamais une seule avant), au moins une somme
 *     à parenthèses (« parenthèses donc produit » résolvait 100 % des
 *     tableaux), expressions distinctes ; libellé « Vrai ou faux : somme ou
 *     produit » ; consigne sans méthode ; repli fixe supprimé.
 *   - KaTeX partout, lettre comprise (tex()), « x^2 » et « - » dans le LaTeX.
 *   - Pas de rappel brouillon : l'attendu se fait de tête (lire la dernière
 *     opération, aucun calcul).
 *   - Mode auto, À ARBITRER : 3x + 2 et 5(x + 4), toute lettre, ne sortent
 *     jamais. Ce sont les sacrifiés de la fiche de gen_1229 (même notion en 3e,
 *     « sacrifiés partagés » du cadrage 158-163), pour qu'une future fiche de
 *     158 puisse reprendre la même.
 *   - Mode auto : au QCM, un produit est demandé deux fois sur trois (comme
 *     1229) ; quand c'est une somme, la clé est une somme à parenthèses une
 *     fois sur deux. Résidu mesuré : « sans parenthèses, donc une somme »
 *     désigne la clé seule dans 17,1 % des tirages (88,5 % avant), tous des
 *     QCM « … une somme ? » à clé sans parenthèses.
 *
 * Cartes (2026-09-23) :
 *   ident « Somme ou produit ? La dernière opération décide »
 *     ident_classer (Classer) : Somme / Produit, trois expressions chacune.
 *     ident_qcm_unique (QCM) : « Laquelle de ces expressions est un produit ? »
 *   vf « Vrai ou faux : somme ou produit »
 *     vf_tableau_vf (Vrai/Faux) : quatre lignes « … est une somme. » / « … est
 *     un produit. ».
 *
 * INVARIANT : la nature d'une expression est celle de sa FAMILLE, et elle est
 * relue dans l'écriture par un second chemin (un + ou un − hors parenthèses,
 * ailleurs qu'en tête, fait une somme) : throw si les deux divergent.
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeVraiFaux, makeClasser,
} from '../../../utils';

const ATTENDU_ID = 158;

const L_IDENT = 'Somme ou produit ? La dernière opération décide';
const L_VF = 'Vrai ou faux : somme ou produit';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Une lettre par tirage (x trois fois sur six), comme gen_1229.
const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];

/** Coefficient devant la lettre : jamais « 1x ». */
const mono = (c, L) => (c === 1 ? L : `${c}${L}`);

// ═════════ Familles (écritures LaTeX, la nature dérive de la famille) ═════════
// k : coefficient ou facteur (2 à 9) ; a, b : nombres (1 à 9).
// code = l'erreur de l'élève qui prendrait cette SOMME pour un produit ; un
// produit pris pour une somme, c'est toujours plus_dedans.

const SOMMES = {
    kx_plus_a: { paren: false, code: 'fois_dedans', tex: (L, k, a) => `${k}${L} + ${a}` },
    kx_moins_a: { paren: false, code: 'fois_dedans', tex: (L, k, a) => `${k}${L} - ${a}` },
    a_moins_kx: { paren: false, code: 'fois_dedans', tex: (L, k, a) => `${a} - ${k}${L}` },
    kx2_plus_ax: { paren: false, code: 'carre_donc_produit', tex: (L, k, a) => `${k}${L}^2 + ${mono(a, L)}` },
    k_paren_plus_b: { paren: true, code: 'parentheses_donc_produit', tex: (L, k, a, b) => `${k}(${L} + ${a}) + ${b}` },
    paren_moins_paren: { paren: true, code: 'parentheses_donc_produit', tex: (L, k, a, b) => `(${L} + ${a}) - (${k}${L} + ${b})` },
};
const PRODUITS = {
    k_paren_plus: { code: 'plus_dedans', tex: (L, k, a) => `${k}(${L} + ${a})` },
    k_paren_moins: { code: 'plus_dedans', tex: (L, k, a) => `${k}(${L} - ${a})` },
    paren_paren: { code: 'plus_dedans', tex: (L, k, a, b) => `(${L} + ${a})(${L} - ${b})` },
    x_paren: { code: 'plus_dedans', tex: (L, k, a) => `${L}(${k}${L} + ${a})` },
    paren_x: { code: 'plus_dedans', tex: (L, k, a) => `(${k}${L} + ${a})${L}` },
};
const FAMILLES = { somme: SOMMES, produit: PRODUITS };
const SOMMES_PAREN = Object.keys(SOMMES).filter((id) => SOMMES[id].paren);
const SOMMES_SANS = Object.keys(SOMMES).filter((id) => !SOMMES[id].paren);

/** Sacrifiés de la fiche de gen_1229 (même notion en 3e) : 3x + 2 et 5(x + 4), toute lettre. */
const estSacrifie = (ecriture, L) => ecriture === `3${L} + 2` || ecriture === `5(${L} + 4)`;

/** Second chemin : la nature LUE dans l'écriture (un + ou un − hors parenthèses, pas en tête). */
const natureLue = (ecriture) => {
    let profondeur = 0;
    for (let i = 0; i < ecriture.length; i++) {
        const c = ecriture[i];
        if (c === '(') profondeur += 1;
        else if (c === ')') profondeur -= 1;
        else if (profondeur === 0 && i > 0 && (c === '+' || c === '-')) return 'somme';
    }
    return 'produit';
};

/** Une expression de la famille donnée, écrite avec la lettre L ; nature vérifiée. */
const tirerItem = (L, nature, id) => {
    const fam = FAMILLES[nature][id];
    for (let essai = 0; essai < 50; essai++) {
        const ecriture = fam.tex(L, randInt(2, 9), randInt(1, 9), randInt(1, 9));
        if (estSacrifie(ecriture, L)) continue;
        if (natureLue(ecriture) !== nature) throw new Error(`gen_158 : nature de ${ecriture}`);
        return { famille: id, nature, ecriture, code: fam.code };
    }
    throw new Error(`gen_158 : famille ${id} sans tirage`);
};

/** n familles de sommes distinctes dont AU MOINS UNE à parenthèses. */
const famillesSommes = (n) => {
    const paren = randomChoice(SOMMES_PAREN);
    const autres = shuffle(Object.keys(SOMMES).filter((id) => id !== paren)).slice(0, n - 1);
    return shuffle([paren, ...autres]);
};
const famillesProduits = (n) => shuffle(Object.keys(PRODUITS)).slice(0, n);

const MOT = { somme: 'une somme', produit: 'un produit' };

// ═════════ Carte « ident » : classer + « Laquelle est … ? » ═════════

const genIdentClasser = () => {
    for (let essai = 0; essai < 50; essai++) {
        const L = randomChoice(LETTRES);
        const sommes = famillesSommes(3).map((id) => tirerItem(L, 'somme', id));
        const produits = famillesProduits(3).map((id) => tirerItem(L, 'produit', id));
        if (new Set([...sommes, ...produits].map((it) => it.ecriture)).size !== 6) continue;
        const payload = makeClasser([
            { name: 'Somme', items: sommes.map((it) => tex(it.ecriture)) },
            { name: 'Produit', items: produits.map((it) => tex(it.ecriture)) },
        ], '<p><b>Classe chaque expression : somme ou produit ?</b></p>', opts(L_IDENT));
        payload._meta.params = {
            lettre: L,
            sommes: sommes.map(({ famille, ecriture }) => ({ famille, ecriture })),
            produits: produits.map(({ famille, ecriture }) => ({ famille, ecriture })),
        };
        return payload;
    }
    throw new Error('gen_158 : classer sans tirage');
};

const genIdentQcm = () => {
    for (let essai = 0; essai < 50; essai++) {
        const L = randomChoice(LETTRES);
        const nature = randomChoice(['produit', 'produit', 'somme']);
        const autre = nature === 'produit' ? 'somme' : 'produit';
        // Somme demandée : la clé est une somme à parenthèses une fois sur deux,
        // sinon « sans parenthèses, donc une somme » la trouverait à coup sûr.
        const idCle = nature === 'produit'
            ? randomChoice(Object.keys(PRODUITS))
            : randomChoice([...SOMMES_PAREN, ...SOMMES_PAREN, ...SOMMES_SANS]);
        const bon = tirerItem(L, nature, idCle);
        // Produit demandé : au moins une somme à parenthèses parmi les distracteurs.
        const faux = (autre === 'somme' ? famillesSommes(3) : famillesProduits(3))
            .map((id) => tirerItem(L, autre, id));
        const choix = [bon, ...faux].map((it) => tex(it.ecriture));
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, choix[0],
            `<p><b>Laquelle de ces expressions est ${MOT[nature]} ?</b></p>`, opts(L_IDENT));
        payload.contenu = { ...payload.contenu, sousType: nature };
        payload._meta.params = {
            lettre: L, nature, cle: { famille: bon.famille, ecriture: bon.ecriture },
            distracteurs: faux.map((f) => ({ code: f.code, famille: f.famille, ecriture: f.ecriture })),
        };
        return payload;
    }
    throw new Error('gen_158 : QCM sans tirage');
};

// ═════════ Carte « vf » : tableau vrai/faux ═════════

const genVf = () => {
    for (let essai = 0; essai < 50; essai++) {
        const L = randomChoice(LETTRES);
        // deux sommes (dont au moins une à parenthèses) et deux produits
        const items = [
            ...famillesSommes(2).map((id) => tirerItem(L, 'somme', id)),
            ...famillesProduits(2).map((id) => tirerItem(L, 'produit', id)),
        ];
        if (new Set(items.map((it) => it.ecriture)).size !== 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = shuffle(items.map((it, i) => {
            const dite = verites[i] ? it.nature : (it.nature === 'somme' ? 'produit' : 'somme');
            return {
                ligne: `${tex(it.ecriture)} est ${MOT[dite]}.`,
                vraie: verites[i],
                code: verites[i] ? null : it.code,
                ecriture: it.ecriture,
            };
        }));
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), lignes.map((l) => (l.vraie ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload._meta.params = {
            lettre: L,
            lignes: lignes.map(({ ecriture, vraie }) => ({ ecriture, vraie })),
            distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })),
        };
        return payload;
    }
    throw new Error('gen_158 : Vrai/Faux sans tirage');
};

// ═════════ Contrat distracteurs (référence de revue, jamais lu à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'deux natures, somme et produit (les mots de l\'attendu) ; une différence est une somme (cours : 2x − 9 = 2x + (−9)) ; ni quotient, ni coefficient, ni terme constant',
        'une lettre par tirage (x trois fois sur six, n, t, y) ; k de 2 à 9, a et b de 1 à 9, jamais « 1x »',
        'sommes : kx + a, kx − a, a − kx, kx² + ax, k(x + a) + b, (x + a) − (kx + b) ; produits : k(x + a), k(x − a), (x + a)(x − b), x(kx + a), (kx + a)x',
        'nature de chaque expression relue dans l\'écriture par un second chemin (throw) ; sacrifiés 3x + 2 et 5(x + 4), toute lettre',
    ],
    ident_qcm_unique: {
        support: 'quatre expressions distinctes d\'une même lettre, une seule de la nature demandée (produit deux fois sur trois, contenu.sousType)',
        familles: {
            plus_dedans: 'une somme est demandée et l\'élève prend un produit comme k(x + a) pour une somme, à cause du + dans la parenthèse (5(x + 4) → somme) : erreur PRINCIPALE, gen_1229 et cadrage 1222-1228',
            fois_dedans: 'un produit est demandé et l\'élève prend kx + a, kx − a ou a − kx pour un produit, à cause du produit caché dans kx (3x + 2 → produit) : gen_1229',
            parentheses_donc_produit: 'un produit est demandé et l\'élève prend une somme à parenthèses, k(x + a) + b ou (x + a) − (kx + b), pour un produit : « des parenthèses, donc un produit », gen_1229',
            carre_donc_produit: 'un produit est demandé et l\'élève prend kx² + ax pour un produit, à cause du carré (x² = x × x) : cadrage 1222-1228',
        },
        supprimees: {
            quotient: 'sortie avec le quotient : hors plafond (« somme, produit »), absent du cours de 4e, et « 8y/7 » se lit aussi (8/7)y, un produit',
            nature_en_trois_mots: 'l\'ancien QCM « Quelle est la nature de … ? » [une somme ; un produit ; un quotient] : sans le quotient il ne reste que deux choix, et « parenthèses donc produit » y réussissait 88,5 %',
            corrige_l_affirmation: 'vf_qcm_unique : un distracteur sur trois reprenait l\'affirmation que l\'énoncé déclarait fausse, un autre était le quotient',
            fiche_d_identite: 'fiche_qcm_unique (coefficient, terme constant) : hors attendu et hors cours ; k + a et k × a ne correspondaient à aucune erreur',
        },
        garantie: 'trois distracteurs garantis par construction : trois familles distinctes de l\'autre nature, donc trois écritures distinctes entre elles et de la clé (vérifié, re-tirage borné sinon). Produit demandé : au moins un distracteur est une somme à parenthèses, « parenthèses donc produit » ne désigne donc jamais la clé seule. Somme demandée : la clé est une somme à parenthèses une fois sur deux, sinon « sans parenthèses, donc une somme » la trouverait à coup sûr (les cinq familles de produits ont des parenthèses).',
    },
    ident_classer: 'deux catégories Somme et Produit, trois expressions chacune, familles distinctes, au moins une somme à parenthèses',
    vf_tableau_vf: 'quatre lignes « … est une somme. » / « … est un produit. » : deux sommes (dont au moins une à parenthèses) et deux produits, une à trois vraies ; une ligne fausse porte le code de l\'erreur qui la ferait cocher Vrai',
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    ident_classer: genIdentClasser,
    ident_qcm_unique: genIdentQcm,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'ident', label: L_IDENT, formatsList: buildFormats('ident') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
